import React, { useRef } from 'react';
import { connect } from 'react-redux';

import { toggleSpinner } from 'apps/auth/actions/appAction';
import PopupNew, { type PopupNewHandle } from 'apps/auth/components/common/Popup/PopupNew';
import GAMigrationModal from 'apps/auth/components/ga/GAMigrationModal';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { GA_ACCOUNT, GA_CARD, PROFILE, REQUEST, SEND } from 'apps/auth/constants/Constants';
import { AUTH_GA_ACCOUNTS, AUTH_GA_CARDS, AUTH_PROFILE, AUTH_REQUEST_MAIN_LINK, AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import type { RootState } from 'apps/auth/redux/store';

import { Container, Menu } from './styles';
import { MenuItem } from './subcomponents/MenuItem';

type ModalNewHandle = {
    open: (content?: React.ReactNode | null) => void;
    close: () => void;
};

type GaMigrationCountry = {
    id?: string | number;
    nameEn?: string;
};

interface OwnProps {
    currentPage?: string;
    isGA?: boolean;
}

interface StateProps {
    hasGlobalAccount?: boolean;
    hasCardTab?: boolean;
    gaMigration?: boolean;
    switchOffGlobalAccountTabs?: boolean;
    country?: GaMigrationCountry;
    transferTitle?: string;
    requestTitle?: string;
    ga_cardsTitle?: string;
    ga_accountsTitle?: string;
    profileTitle?: string;
}

type MenuMessages = {
    sideBarMenu_transfer?: string;
    sideBarMenu_request?: string;
    sideBarMenu_ga_cards?: string;
    sideBarMenu_ga_accounts?: string;
    sideBarMenu_profile?: string;
};

const actions = {
    toggleSpinner,
};

type DispatchProps = typeof actions;
type MenuSideBarProps = OwnProps & StateProps & DispatchProps;

type MenuConfig = {
    isSelected: boolean;
    link: string;
    title?: string;
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    testId: string;
    iconId: string;
    isHidden?: boolean;
    isHiddenMobile?: boolean;
};

const MenuSideBarPureComponent: React.FC<MenuSideBarProps> = (props) => {
    const popupFullwidthRef = useRef<PopupNewHandle | null>(null);
    const modalRef = useRef<ModalNewHandle | null>(null);

    const openGAMigrationModal = () => {
        popupFullwidthRef.current?.open(<GAMigrationModal modalRef={modalRef} popupFullwidthRef={popupFullwidthRef} country={props.country} />);
    };

    const sideBarClick = (event: React.MouseEvent<HTMLAnchorElement>, url: string, isGaLink: boolean) => {
        if (props.hasGlobalAccount) {
            if ((!props.isGA && isGaLink) || (props.isGA && !isGaLink)) {
                event.preventDefault();
                if (props.gaMigration) {
                    openGAMigrationModal();
                    return;
                }
                props.toggleSpinner(true);
                if (__isBrowser__) {
                    window.location.href = url;
                }
            }
        }
    };

    const menuItems: MenuConfig[] = [
        {
            isSelected: props.currentPage === SEND,
            link: AUTH_SEND_MAIN_LINK,
            title: props.transferTitle,
            onClick: (event) => sideBarClick(event, AUTH_SEND_MAIN_LINK, false),
            testId: 'send',
            iconId: 'transfers',
        },
        {
            isSelected: props.currentPage === REQUEST,
            link: AUTH_REQUEST_MAIN_LINK,
            title: props.requestTitle,
            onClick: (event) => sideBarClick(event, AUTH_REQUEST_MAIN_LINK, false),
            testId: 'request',
            iconId: 'receipt',
        },
        {
            isSelected: props.currentPage === GA_CARD,
            link: AUTH_GA_CARDS,
            title: props.ga_cardsTitle,
            onClick: (event) => sideBarClick(event, AUTH_GA_CARDS, true),
            testId: 'ga_cards',
            isHidden: !props.hasCardTab,
            iconId: 'cardp',
        },
        {
            isSelected: props.currentPage === GA_ACCOUNT,
            link: AUTH_GA_ACCOUNTS,
            title: props.ga_accountsTitle,
            onClick: (event) => sideBarClick(event, AUTH_GA_ACCOUNTS, true),
            testId: 'ga-account',
            isHidden: !props.hasGlobalAccount,
            iconId: 'safe',
        },
        {
            isSelected: props.currentPage === PROFILE,
            link: AUTH_PROFILE,
            title: props.profileTitle,
            onClick: (event) => sideBarClick(event, AUTH_PROFILE, false),
            testId: 'profile',
            iconId: 'person',
            isHiddenMobile: true,
        },
    ];

    return (
        <Container>
            <Menu>
                {menuItems.map((item, index) =>
                    item.isHidden ? null : <MenuItem {...item} key={index} isHidden={item.isHidden} isHiddenMobile={item.isHiddenMobile} />,
                )}
            </Menu>
            <PopupNew ref={popupFullwidthRef} fullwidthPopup />
            <ModalNew ref={modalRef} />
        </Container>
    );
};

const mapStateToProps = (state: RootState): StateProps => {
    const authState = state.auth as { hasGlobalAccount?: boolean; hasCardTab?: boolean };
    const extraState = state.extra as { gaMigration?: boolean; switchOffGlobalAccountTabs?: boolean; userCountry?: GaMigrationCountry };
    const messageState = state.messages as { pageMessages?: MenuMessages } | undefined;
    const pageMessages = messageState?.pageMessages ?? {};
    return {
        hasGlobalAccount: authState.hasGlobalAccount,
        hasCardTab: authState.hasCardTab,
        gaMigration: extraState.gaMigration,
        switchOffGlobalAccountTabs: extraState.switchOffGlobalAccountTabs,
        country: extraState.userCountry,
        transferTitle: pageMessages.sideBarMenu_transfer,
        requestTitle: pageMessages.sideBarMenu_request,
        ga_cardsTitle: pageMessages.sideBarMenu_ga_cards,
        ga_accountsTitle: pageMessages.sideBarMenu_ga_accounts,
        profileTitle: pageMessages.sideBarMenu_profile,
    };
};

const MenuSideBar = connect(mapStateToProps, actions)(MenuSideBarPureComponent as React.ComponentType<any>);
export default MenuSideBar;
